// src/features/projects/ProjectsPortfolioList.jsx
//
// Task 8714 — canonical Projects portfolio list. Mounted by both the Projects
// Portfolio landing (ProjectsPage) and Home's "Portfolio in motion" pane so a
// project renders with identical values, tokens and Briefing peek behavior.
//
// Input rows are ENRICHED projects (raw project + `portfolio` from
// portfolioRowModel.enrichPortfolioProject). This component never converts
// money, picks targets or computes seats; it only presents the contract.
//
// Seven data columns; only Home omits the trailing LINKS/caret slot.

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildProjectUpdatesUrl } from "../../services/buildUrl.js";
import { T, PROJECT_STATUS } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { CollectionSortHeader, CollectionSortCaret } from "../../v2/components/CollectionSortHeader.jsx";
import { LIST_PRESENTATION, listHeaderEdges, COLLECTION_GEOMETRY, collectionRowHeight } from "../../v2/components/listPresentation.js";
import { ProgressDots } from "../../v2/components/ProgressDots.jsx";
import { StatusPill } from "../../v2/components/StatusPill.jsx";
import { fmtDate } from "../../utils/format.js";
import { ProjectInlineExpanded } from "./ProjectInlineExpanded.jsx";
import { ProjectBriefingPeek } from "./briefing/ProjectBriefingPeek.jsx";
import { typePillMeta, phaseDots } from "./briefing/landingModel.js";
import { formatRecordAmount } from "./projectRecord.js";
import { PortfolioLinkSlots } from "./connections/PortfolioLinkSlots.jsx";
import { portfolioLinksWidth, PORTFOLIO_LINK_PADDING } from "./connections/portfolioLinkSlots.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { useListHeaderStuck } from "../../v2/hooks/useListHeaderStuck.js";
import { pillMinWidth } from "../../v2/utils/pillMinWidth.js";
import { isTerminalProject } from "../../lib/projectStage.js";
import { ProjectStagePill } from "./ProjectStageControl.jsx";
import { monthYear } from "./drawer/projectHeaderModel.js";
import { useCollectionCellTitles } from "../../v2/hooks/useCollectionCellTitles.js";
import { MilestoneSignal, budgetHealthColor } from "./ProjectDisplaySignals.jsx";

export const PORTFOLIO_DENSITY_COMFORTABLE = "comfortable";
export const PORTFOLIO_DENSITY_COMPACT = "compact";

// Target sort keys accepted by the landing (fdobDate is current; the legacy
// targetCompletionDate saved preference stays compatible).
const TARGET_SORT_KEYS = new Set(["fdobDate", "targetCompletionDate"]);

const TH_STYLE = {
  padding: "10px 14px",
  textAlign: "center",
  fontSize: T.fsXxs,
  fontWeight: T.fwBold,
  lineHeight: "normal",
  color: "var(--v2-mid)",
  textTransform: "uppercase",
  letterSpacing: ".07em",
  borderBottom: "none",
  boxShadow: "0 1px 0 var(--v2-line)",
  whiteSpace: "nowrap",
  background: "var(--v2-tint)",
  userSelect: "none",
};

const COL_WIDTHS = [203, 120, 120, 232, 140, 140, 115, 40];
// Home uses Standard phase dots and no actions, not collection/peek geometry.
const HOME_COL_WIDTHS = [180, 132, 110, 166, 160, 120, 108];
const HOME_CELL_PADDING = 10;

const FALLBACK_PILL_WIDTHS = { type: 112, status: 88 };
const statusFor = p => isTerminalProject(p)
  ? { label: p.stage === "completed" ? `Completed ${monthYear(p.completedOn) || ""}`.trim() : "Cancelled", color: T.mid, bg: T.lightGray }
  : p.stage === "intake" ? null : PROJECT_STATUS[p.statusOverall];

function useProjectPillWidths(projects, rootRef) {
  const [typography, setTypography] = useState(null);
  useLayoutEffect(() => {
    let alive = true;
    const measureFont = () => {
      if (!alive || typeof getComputedStyle !== "function") return;
      const pill = rootRef.current?.querySelector('[data-testid^="project-type-pill-"] > span, [data-testid^="project-status-"] > span');
      if (!pill) return;
      const computed = getComputedStyle(pill);
      const next = Object.fromEntries(["fontFamily", "fontSize", "fontWeight", "fontStyle", "letterSpacing", "borderLeftWidth", "borderRightWidth"].map(key => [key, computed[key]]));
      // New object once after fonts settle also invalidates canvas measurements.
      setTypography(next);
    };
    measureFont();
    const fonts = typeof document === "undefined" ? null : document.fonts;
    fonts?.ready?.then(measureFont, () => {});
    fonts?.addEventListener?.("loadingdone", measureFont);
    return () => { alive = false; fonts?.removeEventListener?.("loadingdone", measureFont); };
  }, [projects, rootRef]);
  return useMemo(() => ({
    type: pillMinWidth([...new Set(projects.filter(p => p.projectType).map(p => typePillMeta(p.projectType).label))], 112, typography),
    status: pillMinWidth([...new Set(projects.map(p => statusFor(p)?.label).filter(Boolean))], 88, typography),
  }), [projects, typography]);
}

const COLUMNS = [
  { id: "project", label: "PROJECT", sortKey: "name", align: "left" },
  { id: "type", label: "TYPE", sortKey: "projectType", align: "left" },
  { id: "target", label: "TARGET", sortKey: "fdobDate", align: "center" },
  { id: "phase", label: "PHASE", sortKey: "phase", align: "left" },
  { id: "milestone", label: "NEXT MILESTONE", sortKey: null, align: "left" },
  { id: "budget", label: "BUDGET", sortKey: "approvedBudget", budget: true, align: "center" },
  { id: "status", label: "STATUS", sortKey: "statusOverall", align: "left" },
];

// ─── Header ───────────────────────────────────────────────────────────────────

export function SortableTH({ children, colKey, sortKey, sortDir, onSort, style = TH_STYLE, activeKeys = null }) {
  const isActive = sortKey === colKey || Boolean(activeKeys && activeKeys.has(sortKey));
  function handleClick() {
    if (isActive) onSort(colKey, -sortDir);
    else onSort(colKey, 1);
  }
  return (
    <CollectionSortHeader active={isActive} direction={sortDir}
      style={{
        ...style,
        cursor: "pointer",
        ...(isActive
          ? { borderBottom: "none" }
          : { borderBottom: style.borderBottom, background: style.background }),
      }}
      aria-sort={isActive ? (sortDir > 0 ? "ascending" : "descending") : "none"}
      onClick={handleClick}
      data-testid={`projects-sort-${colKey}`}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
        <CollectionSortCaret testId={`projects-sort-indicator-${colKey}`} />
      </div>
    </CollectionSortHeader>
  );
}

// ─── Cells ────────────────────────────────────────────────────────────────────

const oneLine = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const gapText = { color: T.light };

export function PhaseDots({ project, collection = false, compact = false }) {
  if (project.stage && project.stage !== "active") return <div data-testid={`project-phase-${project.id}`} className="project-phase-stage"
    style={{ ...oneLine, textAlign: collection ? "center" : undefined }}
    title={`${project.stage === "on_hold" ? "On hold" : project.stage}${project.stage === "on_hold" && phaseDots(project.phase).known ? ` at ${phaseDots(project.phase).label}` : ""}`}>
    <ProjectStagePill stage={project.stage} />
    {(!collection || !compact) && project.stage === "on_hold" && phaseDots(project.phase).known && <span style={{ color: T.mid }}> at {phaseDots(project.phase).label}</span>}
  </div>;
  const info = phaseDots(project.phase);
  const index = info.steps.findIndex(s => s.state === "current") + 1;
  const caption = !collection || !info.known || info.complete ? info.label : `${info.label} · ${index} of 7`;
  return (
    <div data-testid={`project-phase-${project.id}`} style={{ minWidth: 0, ...(collection ? { textAlign: "center" } : { display: "flex", alignItems: "center" }) }}
      title={info.known ? caption : `${info.label} (not on the seven-phase scale)`}>
      <ProgressDots steps={info.steps} complete={info.complete} compact={compact}
        retained={!collection} caption={caption}
        shortCaption={info.complete ? "7/7" : info.known ? `${index}/7` : info.label}
        tooltip={info.known ? caption : `${info.label} (not on the seven-phase scale)`}
        dotsTestId={`project-phase-dots-${project.id}`} />
    </div>
  );
}

function TargetCell({ project }) {
  const pf = project.portfolio || {};
  return (
    <div data-testid={`project-target-${project.id}`} title={pf.targetLabel || "Target"} style={{ ...oneLine, minWidth: 0, fontVariantNumeric: "tabular-nums", ...(!pf.targetDate && gapText) }}>
        {pf.targetDate ? fmtDate(pf.targetDate) : "Data gap"}
    </div>
  );
}

function MilestoneCell({ project }) {
  const m = project.portfolio?.milestone || null;
  const err = project.portfolio?.milestoneError;
  if (!m) {
    return (
      <span data-testid={`project-next-milestone-${project.id}`} title={err ? "Milestones could not be loaded." : undefined}
        style={{ ...gapText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
        {err ? "Milestones unavailable" : "No upcoming milestone"}
      </span>
    );
  }
  const date = m.date ? fmtDate(m.date) : "Date not set";
  return (
    <div data-testid={`project-next-milestone-${project.id}`} style={{ minWidth: 0, lineHeight: "14px" }} title={`${m.label} · ${date}`}>
       <div style={oneLine}>{m.label}<span style={LIST_PRESENTATION.secondary}>{` · ${date}`}</span></div>
       <div><MilestoneSignal milestone={project.nextMilestone} timeZone={project.projectTimeZone} /></div>
    </div>
  );
}

export function formatPortfolioSeats(seats) {
  if (seats == null || !Number.isFinite(Number(seats))) return null;
  const n = Math.round(Number(seats));
  if (n > 0) return `+${n.toLocaleString("en-US")}`;
  if (n < 0) return `-${Math.abs(n).toLocaleString("en-US")}`;
  return "0";
}

function BudgetCell({ project, canSeeBudget }) {
  const pf = project.portfolio || {};
  let text; let title; let muted = true;
  if (!canSeeBudget || pf.budgetState === "restricted") {
    text = "Restricted"; title = "Budget is restricted for your role.";
  } else if (project.financialsTracked === false) {
    text = "n/a"; title = "Financials are not tracked for this project.";
  } else if (pf.budgetState === "missing_rate") {
    text = "Missing FX rate"; title = "No exchange rate available to convert this budget to USD.";
  } else if (pf.budgetState === "available" && pf.budgetUsd != null) {
    text = formatRecordAmount(pf.budgetUsd, "USD"); title = "Approved budget, converted to USD"; muted = false;
  } else {
    text = "Data gap"; title = "No approved budget recorded.";
  }
  return (
    <span data-testid={`project-budget-${project.id}`} data-state={!canSeeBudget ? "restricted" : pf.budgetState || "missing_budget"} title={title}
      style={{ ...oneLine, display: "block", color: muted ? T.light : "var(--v2-mid)", fontVariantNumeric: "tabular-nums" }}>
      {muted ? text : (
        <span style={{ display: "inline-grid", gridTemplateColumns: "64px 28px", gap: 4, maxWidth: "100%", verticalAlign: "middle" }}>
          <span data-testid="project-budget-amount" style={{ ...oneLine, textAlign: "right" }}>{text.replace(/ USD$/, "")}</span>{" "}
          <span data-testid="project-budget-currency" style={{ textAlign: "left" }}>USD</span>
        </span>
      )}
       {!muted && pf.budgetHealth && <span role="img" aria-label={pf.budgetHealth.ratio == null ? "Not tracked" : `Forecast ${Number((pf.budgetHealth.ratio * 100).toFixed(2))}% of budget`}
         title={pf.budgetHealth.ratio == null ? "Not tracked" : `Forecast ${Number((pf.budgetHealth.ratio * 100).toFixed(2))}% of budget`}
         style={{ display: "inline-block", marginLeft: 4, width: 6, height: 6, borderRadius: "50%", background: budgetHealthColor(pf.budgetHealth) }} />}
    </span>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

export function ProjectRow({ p, onTogglePeek, onOpenFull, onOpenRecord, onOpenRecordNewTab, homeMotion = false, showConnections = true, rowHeight, collection = false, standardPhase = false, compact = false, peekOpen = false, dimmed = false, fullExpanded = false, canSeeBudget, interactive = true, pillWidths = FALLBACK_PILL_WIDTHS }) {
  const statusMeta = statusFor(p);
  const cell = { padding: `0 ${homeMotion ? HOME_CELL_PADDING : 14}px`, verticalAlign: "middle", fontSize: T.fsBase, color: "var(--v2-mid)", lineHeight: "22px", ...oneLine };
  const widths = pillWidths;
  const type = typePillMeta(p.projectType);
  const open = peekOpen || fullExpanded;
  // Round 4: when the host supplies onOpenRecord (Portfolio), row activation
  // opens the shared record drawer instead of the inline peek.
  const drawerMode = typeof onOpenRecord === "function";
  const href = buildProjectUpdatesUrl(p.id, typeof window === "undefined" ? "" : window.location.search);
  const openPage = (e) => {
    e.stopPropagation();
    if (typeof onOpenFull !== "function" || e.metaKey || e.ctrlKey || e.shiftKey || (drawerMode && e.altKey) || e.button !== 0) return;
    e.preventDefault();
    onOpenFull(p.id);
  };
  const activate = (el) => (drawerMode ? onOpenRecord(p.id, el) : onTogglePeek(p.id));
  const toggle = interactive ? (e) => {
    if (homeMotion && drawerMode && (e.metaKey || e.ctrlKey) && typeof onOpenRecordNewTab === "function") {
      e.preventDefault();
      onOpenRecordNewTab(p.id);
      return;
    }
    activate(e.currentTarget);
  } : undefined;

  return (
    <tr
      data-testid={`project-row-${p.id}`}
      className={`projects-landing-row${collection ? " collection-keyboard-row" : ""}${interactive && drawerMode ? " projects-portfolio-drawer-row" : ""}`}
      tabIndex={interactive ? 0 : undefined}
      aria-expanded={interactive && !drawerMode ? open : undefined}
      aria-haspopup={interactive && drawerMode ? "dialog" : undefined}
      aria-label={interactive ? (drawerMode ? `${p.name}, open project record` : `${p.name}, ${peekOpen ? "briefing open" : "open briefing"}`) : undefined}
      onClick={toggle}
      onKeyDown={interactive ? (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(e.currentTarget); }
      } : undefined}
      style={{
        cursor: interactive ? "pointer" : "default",
        background: T.card,
        height: rowHeight,
        maxHeight: rowHeight,
        borderBottom: peekOpen ? "none" : "1px solid var(--v2-hair)",
        opacity: dimmed || isTerminalProject(p) ? T.projectsRowDim : 1,
      }}
      onMouseEnter={interactive ? (e) => { if (!open) e.currentTarget.style.background = T.hoverBg; } : undefined}
      onMouseLeave={interactive ? (e) => { e.currentTarget.style.background = T.card; } : undefined}
    >
      <td data-collection-identity={collection ? "" : undefined} style={cell}>
        <span data-collection-name={collection ? "" : undefined} data-testid={`project-name-chip-${p.id}`} title={collection ? undefined : p.name} style={{ ...LIST_PRESENTATION.name, ...oneLine, display: "block", maxWidth: "100%" }}>
          {interactive && !drawerMode ? (
            <a
              href={href}
              data-testid={`project-name-link-${p.id}`}
              className="projects-name-link"
              title={collection ? undefined : drawerMode ? `Open Updates: ${p.name}` : `Open full project: ${p.name}`}
              onClick={openPage}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              style={{ ...LIST_PRESENTATION.name, textDecoration: "none" }}
            >
              {p.name}
            </a>
          ) : (
            <span data-testid={`project-name-${p.id}`} style={LIST_PRESENTATION.name}>{p.name}</span>
          )}
        </span>
      </td>
      <td style={{ ...cell, textAlign: "center" }}>
        {p.projectType
          ? <StatusPill value={type.label} fg={type.fg} bg={type.bg} testId={`project-type-pill-${p.projectType}`} style={{ width: widths.type, boxSizing: "border-box", verticalAlign: "middle" }} />
          : <span style={gapText}>Not set</span>}
      </td>
      <td style={{ ...cell, textAlign: "center" }}><TargetCell project={p} /></td>
      <td style={cell}><PhaseDots project={p} collection={collection || standardPhase} compact={standardPhase ? false : compact} /></td>
      <td style={cell}><MilestoneCell project={p} /></td>
      <td style={{ ...cell, textAlign: "center" }}><BudgetCell project={p} canSeeBudget={canSeeBudget} /></td>
      <td style={{ ...cell, textAlign: "center" }}>
        {statusMeta
          ? <StatusPill value={statusMeta.label} bg={statusMeta.bg} fg={statusMeta.color} testId={`project-status-${p.id}`} style={{ width: widths.status, boxSizing: "border-box", verticalAlign: "middle" }} />
          : <span data-testid={`project-status-${p.id}`} style={gapText}>Not set</span>}
      </td>
      {!homeMotion && <td style={{ ...cell, textAlign: "center", ...(interactive && drawerMode ? { overflow: "visible", padding: `0 ${PORTFOLIO_LINK_PADDING}px` } : {}) }}>
        {interactive && drawerMode && showConnections && (
          <PortfolioLinkSlots connections={p.connections} />
        )}
        {interactive && !drawerMode && (
          <button
            type="button"
            className="projects-row-caret"
            data-testid={`project-caret-${p.id}`}
            aria-expanded={open}
            aria-label={open ? `Collapse ${p.name}` : `Open briefing for ${p.name}`}
            onClick={(e) => { e.stopPropagation(); onTogglePeek(p.id); }}
            style={{ border: "none", background: "transparent", color: T.mid, cursor: "pointer", fontSize: T.fsSm, padding: 4, lineHeight: 1, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}
          >
            ▾
          </button>
        )}
      </td>}
    </tr>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

const NO_SOURCES = new Map();
const EMPTY = [];

/**
 * Shared portfolio list. `projects` must be enriched (have `portfolio`).
 * Sorting headers are interactive only when `onSort` is provided.
 * Full inline detail renders only when `expandedProjectId` matches (landing).
 */
export function ProjectsPortfolioList({
  projects, onTogglePeek, onOpenFull, onOpenRecord, onOpenRecordNewTab,
  homeMotion = false,
  showConnections = true,
  sortKey = null, sortDir = 1, onSort,
  density = PORTFOLIO_DENSITY_COMFORTABLE,
  collection = false,
  standardPhase = false,
  sticky = true, stickyTop = 0,
  peekProjectId = null, expandedProjectId = null,
  currentRole, onProjectUpdated, onArchived, onCollapseFull,
  projectSourcesByProject = NO_SOURCES, portfolioSources = EMPTY, onSourcesChange, onOpenMyUpdate,
  weeklyRefresh = 0, weeklyArchiveCache, onOpenWeeklyArchive,
  weekState, onRetryWeek, canSeeBudget = false,
  interactive = true,
  emptyMessage = "No projects match your filters.",
  testId = "projects-portfolio-list",
}) {
  const headerScrollRef = useRef(null);
  const rootRef = useRef(null);
  useCollectionCellTitles(rootRef, '[data-testid^="project-row-"] > td:not(:nth-child(4))', collection);
  const bodyScrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [detailViewportWidth, setDetailViewportWidth] = useState(null);
  const hasRows = projects.length > 0;
  const isStuck = useListHeaderStuck({ sentinelRef, headerScrollRef, enabled: sticky && hasRows, invalidate: stickyTop });
  const rowHeight = collection ? collectionRowHeight(density) : density === PORTFOLIO_DENSITY_COMPACT ? 28 : 40;
  const widths = useProjectPillWidths(projects, rootRef);
  // Both split tables and the nested peek table share the same tracks.
  const pillPadding = homeMotion ? HOME_CELL_PADDING * 2 : 28;
  const colWidths = (homeMotion ? HOME_COL_WIDTHS : COL_WIDTHS).map((width, i) => i === 1 ? Math.max(width, widths.type + pillPadding)
    : i === 6 ? Math.max(width, widths.status + pillPadding)
      : i === 7 && showConnections && interactive && typeof onOpenRecord === "function" ? portfolioLinksWidth() : width);
  const tableMinWidth = colWidths.reduce((sum, width) => sum + width, 0);

  useLayoutEffect(() => {
    const scrollport = bodyScrollRef.current;
    if (!scrollport) return undefined;
    const measure = () => {
      if (scrollport.clientWidth > 0) setDetailViewportWidth(scrollport.clientWidth);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(scrollport);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [hasRows]);

  const handleBodyScroll = useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  if (!hasRows) {
    return <div data-testid={`${testId}-empty`} style={{ ...S.emptyState, padding: 48 }}>{emptyMessage}</div>;
  }

  const COLGROUP = (
    <colgroup>
      {colWidths.map((w, i) => <col key={i} style={{ width: homeMotion ? `${w / tableMinWidth * 100}%` : i === 4 ? undefined : w }} />)}
    </colgroup>
  );
  const lifted = { boxShadow: T.projectsPeekShadow, outline: `1px solid ${T.projectsPeekHair}`, outlineOffset: -1 };
  const tableStyle = { width: "100%", minWidth: tableMinWidth, borderCollapse: "collapse", tableLayout: "fixed", fontSize: T.fsBase };

  return (
    <div ref={rootRef} data-testid={testId} className={collection ? "projects-collection" : undefined} data-density={density}>
      {collection && <style>{`
        .projects-collection thead tr, .projects-collection thead th {
          height: ${COLLECTION_GEOMETRY.header}px; box-sizing: border-box;
        }
        .projects-collection thead th { padding-top: 0 !important; padding-bottom: 0 !important; }
        .projects-collection[data-density="compact"] [data-testid^="project-type-pill-"] > span,
        .projects-collection[data-density="compact"] [data-testid^="project-status-"] > span,
        .projects-collection[data-density="compact"] .project-phase-stage > span:first-child {
          height: 20px !important; font-size: ${T.fsXxs}px !important; padding-block: 0 !important;
        }
      `}</style>}
      {sticky && <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} />}
      <div data-testid="projects-list-header-sticky" style={sticky ? { position: "sticky", top: stickyTop, zIndex: 10 } : undefined}>
        <div data-testid="projects-list-header-clip" ref={headerScrollRef} style={{ overflowX: "hidden", background: T.listTint, ...listHeaderEdges(isStuck) }}>
          <OverflowTable pinActions={!homeMotion} overflowScrollRef={bodyScrollRef} data-testid="projects-list-header-table" style={tableStyle}>
            {COLGROUP}
            <thead>
              <tr>
                {COLUMNS.map((col) => {
                  const title = col.budget ? (canSeeBudget ? "Approved budget, converted to USD" : "Budget is restricted for your role") : undefined;
                  const sortable = typeof onSort === "function" && col.sortKey && !(col.budget && !canSeeBudget);
                   const style = homeMotion ? { ...TH_STYLE, paddingInline: HOME_CELL_PADDING } : TH_STYLE;
                  if (!sortable) {
                    return <th key={col.id} style={style} title={title} data-testid={`projects-col-${col.id}`}>{col.label}</th>;
                  }
                  return (
                    <SortableTH key={col.id} style={style} colKey={col.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}
                      activeKeys={col.id === "target" ? TARGET_SORT_KEYS : null}>
                      {col.label}
                    </SortableTH>
                  );
                })}
                {!homeMotion && <th style={{ ...TH_STYLE, paddingInline: PORTFOLIO_LINK_PADDING }} data-testid="projects-col-links">{showConnections && typeof onOpenRecord === "function" ? "LINKS" : <span className="sr-only">Open</span>}</th>}
              </tr>
            </thead>
          </OverflowTable>
        </div>
      </div>

      <div data-testid="projects-list-body-scroll" ref={bodyScrollRef} onScroll={handleBodyScroll} style={{ overflowX: "auto" }}>
        <OverflowTable pinActions={!homeMotion} style={tableStyle}>
          {COLGROUP}
          <tbody>
            {projects.map((p) => {
              const peekOpen = interactive && peekProjectId === p.id;
              const row = (
                <ProjectRow
                  p={p}
                   pillWidths={widths}
                  onTogglePeek={onTogglePeek}
                  onOpenFull={onOpenFull}
                  onOpenRecord={onOpenRecord}
                  onOpenRecordNewTab={onOpenRecordNewTab}
                  homeMotion={homeMotion}
                  showConnections={showConnections}
                  rowHeight={rowHeight}
                  collection={collection}
                  standardPhase={standardPhase}
                  compact={density === PORTFOLIO_DENSITY_COMPACT}
                  peekOpen={peekOpen}
                  dimmed={interactive && peekProjectId != null && !peekOpen}
                  fullExpanded={expandedProjectId === p.id}
                  canSeeBudget={canSeeBudget}
                  interactive={interactive}
                />
              );
              return (
                <React.Fragment key={p.id}>
                  {!peekOpen ? row : (
                    <tr data-testid={`project-open-group-row-${p.id}`}>
                      <td colSpan={colWidths.length} style={{ padding: "2px 0 10px", borderBottom: "1px solid var(--v2-hair)" }}>
                        <div data-testid={`project-open-group-${p.id}`} style={{ background: T.card, borderRadius: 10, ...lifted }}>
                          <OverflowTable pinActions={!homeMotion} style={tableStyle}>
                            {COLGROUP}
                            <tbody>{row}</tbody>
                          </OverflowTable>
                          <div data-testid={`project-peek-row-${p.id}`} style={{ borderTop: `1px solid ${T.projectsHubLine}`, width: detailViewportWidth || "100%", maxWidth: "100%", position: "sticky", left: 0 }}>
                            <ProjectBriefingPeek
                              project={p}
                              weekState={weekState}
                              onRetryWeek={onRetryWeek}
                              canSeeBudget={canSeeBudget}
                              refreshToken={weeklyRefresh}
                              onOpenFull={() => onOpenFull(p.id)}
                              onCollapse={() => onTogglePeek(p.id)}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {expandedProjectId === p.id && (
                    <tr data-testid={`project-expanded-row-${p.id}`}>
                      <td colSpan={colWidths.length} style={{ padding: 0, borderBottom: `1px solid ${T.border}` }}>
                        <div data-testid="project-detail-viewport" style={{ width: detailViewportWidth || "100%", maxWidth: "100%", position: "sticky", left: 0 }}>
                          <ProjectInlineExpanded
                            projectId={p.id}
                            rowData={p}
                            currentRole={currentRole}
                            onProjectUpdated={onProjectUpdated}
                            onCollapse={() => onCollapseFull(p.id)}
                            sourceData={[...(projectSourcesByProject.get(p.id) || []), ...portfolioSources]}
                            portfolioSources={portfolioSources}
                            onSourcesChange={onSourcesChange}
                            onOpenMyUpdate={onOpenMyUpdate}
                            weeklyRefresh={weeklyRefresh}
                            weeklyArchiveCache={weeklyArchiveCache}
                            onOpenWeeklyArchive={onOpenWeeklyArchive}
                            onArchived={onArchived}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </OverflowTable>
      </div>
    </div>
  );
}
